import React, { useEffect, useState } from 'react';
import { supabase, isAdmin } from './supabaseClient';
import Auth from './components/Auth';
import AdminDashboard from './components/AdminDashboard';
import OnboardingForm from './components/OnboardingForm';
import ProfilePage from './components/ProfilePage';
import SetPassword from './components/SetPassword';
import { isInvited } from './invitedEmails';

export default function App() {
  const [session, setSession]             = useState(undefined); // undefined = loading
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [dbAdmin, setDbAdmin]             = useState(false);
  const [previewForm, setPreviewForm] = useState(false);
  const [invited, setInvited] = useState(null); // null = checking, true/false = result
  const [staffView, setStaffView]         = useState('profile'); // 'profile' | 'form'
  const [adminProfileRow, setAdminProfileRow] = useState(null); // submission row when admin views a staff profile
  const [adminOwnProfile, setAdminOwnProfile] = useState(false); // admin viewing their own profile
  
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      // Show set-password prompt when staff first sign in via magic link or invite
      if (event === 'SIGNED_IN' && session) {
        const signedInViaOtp = session.user?.app_metadata?.provider === 'email' &&
          !session.user?.user_metadata?.has_set_password;
        // Only prompt if they came in via OTP/magic link and haven't set a password yet
        if (signedInViaOtp && !isAdmin(session.user?.email) && !dbAdmin) {
          setNeedsPassword(true);
        }
      }
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setNeedsPassword(false);
        setPasswordRecovery(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check if user's email is on the invited list, and whether they're a DB-level admin
  useEffect(() => {
    if (!session) { setInvited(null); setDbAdmin(false); return; }
    const email = session.user?.email;
    if (isAdmin(email)) { setInvited(true); return; }
    // Check DB for year-round/admin flag
    supabase.from('invited_emails').select('is_admin').eq('email', email).maybeSingle()
      .then(({ data }) => { if (data?.is_admin) setDbAdmin(true); });
    isInvited(email).then(setInvited);
  }, [session]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setNeedsPassword(false);
  };

  const handlePasswordSet = async () => {
    // Mark in user metadata that password has been set so we don't prompt again
    await supabase.auth.updateUser({ data: { has_set_password: true } });
    setNeedsPassword(false);
  };

  // Loading
  if (session === undefined) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2D5016, #7BA05B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#fff', fontFamily: "'Nunito', sans-serif", fontSize: '1.1rem', opacity: 0.8 }}>Loading…</div>
      </div>
    );
  }

  // Not signed in
  if (!session) return <Auth />;

  // Password reset — user clicked the reset link in their email
  if (passwordRecovery) {
    return <SetPassword onDone={async () => {
      await supabase.auth.updateUser({ data: { has_set_password: true } });
      setPasswordRecovery(false);
    }} />;
  }

  const email = session.user?.email;

  // Admin — viewing a specific staff member's profile
  if ((isAdmin(email) || dbAdmin) && adminProfileRow) {
    return (
      <ProfilePage
        session={session}
        isAdminView={true}
        adminSubmission={adminProfileRow}
        onBack={() => setAdminProfileRow(null)}
        onSignOut={handleSignOut}
      />
    );
  }

  // Admin — viewing their own profile
  if ((isAdmin(email) || dbAdmin) && adminOwnProfile) {
    return (
      <ProfilePage
        session={session}
        onSignOut={handleSignOut}
        onGoToForm={() => { setAdminOwnProfile(false); setPreviewForm(true); }}
        onBack={() => setAdminOwnProfile(false)}
      />
    );
  }

  // Admin — form preview mode
  if ((isAdmin(email) || dbAdmin) && previewForm) {
    return (
      <div>
        <div style={{ background: '#2D5016', color: '#fff', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 700, position: 'sticky', top: 0, zIndex: 9999 }}>
          <span>👁 Admin preview mode — changes here will save to your account</span>
          <button onClick={() => setPreviewForm(false)} style={{ padding: '6px 16px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
            ← Back to dashboard
          </button>
        </div>
        <OnboardingForm session={session} onSignOut={handleSignOut} onBack={() => setPreviewForm(false)} />
      </div>
    );
  }

  // Admin — main dashboard
  if (isAdmin(email) || dbAdmin) {
    return (
      <AdminDashboard
        session={session}
        onSignOut={handleSignOut}
        onPreview={() => setPreviewForm(true)}
        onViewProfile={(row) => setAdminProfileRow(row)}
        onMyProfile={() => setAdminOwnProfile(true)}
      />
    );
  }

  // First magic link login — prompt to set password
  if (needsPassword) {
    return <SetPassword onDone={handlePasswordSet} />;
  }

 // Still checking invited status
    if (invited === null) {
     return (
       <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2D5016, #7BA05B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
         <div style={{ color: '#fff', fontFamily: "'Nunito', sans-serif", fontSize: '1.1rem', opacity: 0.8 }}>Loading…</div>
       </div>
     );
    }

 // Normal staff view — only allow invited emails
    if (!invited && !isAdmin(email)) {
     return (
       <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2D5016, #7BA05B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Nunito', sans-serif" }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔒</div>
           <h2 style={{ color: '#2D5016', fontFamily: "'Playfair Display', serif" }}>Access Restricted</h2>
           <p style={{ color: '#6B7F5A', lineHeight: 1.6 }}>This portal is for invited Camp Phillip SALT staff only. If you think this is a mistake, contact <strong>eli@campphillip.com</strong>.</p>
           <button onClick={handleSignOut} style={{ marginTop: 20, padding: '11px 28px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer' }}>Sign out</button>
         </div>
       </div>
     );
    }
  if (staffView === 'form') {
    return <OnboardingForm session={session} onSignOut={handleSignOut} onBack={() => setStaffView('profile')} />;
  }
  return (
    <ProfilePage
      session={session}
      onSignOut={handleSignOut}
      onGoToForm={() => setStaffView('form')}
    />
  );
  }